import { Droplet, Plus, Pencil, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { LocacoesStatCard } from "./LocacoesStatCard";
import type { AbastecimentoDto, ContratoDto, MaquinarioDto, PeriodRange, RegistroHorimetroDto } from "@/services/locacoes";
import { COMBUSTIVEL_LABELS } from "@/services/locacoes";
import { formatDate, formatNumber } from "@/utils/format";
import {
  computeConsumoMedioAbastecimentosPeriodo,
  filterAbastecimentosByPeriod,
  filterRegistrosParaConsumoContrato,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
  resolveLitrosPorHoraAbastecimento,
  resolveMaquinaNome,
} from "./locacoesUtils";

interface Props {
  abastecimentos: AbastecimentoDto[];
  maquinarios: MaquinarioDto[];
  registrosHorimetro?: RegistroHorimetroDto[];
  contratos?: ContratoDto[];
  combustivelPagoCliente?: boolean;
  period: PeriodRange;
  onNovo: () => void;
  onEdit: (item: AbastecimentoDto) => void;
  onDelete: (item: AbastecimentoDto) => void;
}

export function TabAbastecimentos({
  abastecimentos,
  maquinarios,
  registrosHorimetro = [],
  contratos = [],
  combustivelPagoCliente = false,
  period,
  onNovo,
  onEdit,
  onDelete,
}: Props) {
  const maquinaIds = useMemo(() => maquinarios.map((m) => m.id), [maquinarios]);
  const registrosConsumo = useMemo(
    () => filterRegistrosParaConsumoContrato(contratos, registrosHorimetro),
    [contratos, registrosHorimetro],
  );
  const filtered = filterAbastecimentosByPeriod(abastecimentos, period).filter((a) => maquinaIds.includes(a.maquinaId));
  const litros = filtered.reduce((s, a) => s + a.litros, 0);
  const valor = filtered.reduce((s, a) => s + a.valorTotal, 0);
  const consumoMedio = useMemo(
    () =>
      computeConsumoMedioAbastecimentosPeriodo(abastecimentos, registrosConsumo, period, maquinaIds),
    [abastecimentos, registrosConsumo, period, maquinaIds],
  );
  const lphPorId = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const a of filtered) {
      map.set(
        a.id,
        resolveLitrosPorHoraAbastecimento(a, abastecimentos, {
          registros: registrosConsumo,
          period,
        }),
      );
    }
    return map;
  }, [filtered, abastecimentos, registrosConsumo, period]);

  if (combustivelPagoCliente) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
        <p className="font-semibold">Combustível pago pelo cliente</p>
        <p className="mt-1 text-amber-800">
          Neste contrato o cliente é responsável pelo combustível. Abastecimentos não são registrados pela Semog Locações
          e o consumo (L/h) não é calculado com base nos horímetros deste contrato.
        </p>
      </div>
    );
  }

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard
          title="Abastecimentos"
          value={filtered.length}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-sky-400"
        />
        <LocacoesStatCard
          title="Litros"
          value={`${formatNumber(litros, 0)} L`}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-orange-400"
        />
        <LocacoesStatCard
          title="Valor total"
          value={`R$ ${formatNumber(valor, 2)}`}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard
          title="Consumo médio"
          value={`${formatNumber(consumoMedio, 1)} L/h`}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-purple-400"
        />
      </div>

      <div className="flex justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovo}>
          Novo abastecimento
        </Button>
      </div>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Máquina</th>
              <th className="px-4 py-3">Litros</th>
              <th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Combustível</th>
              <th className="px-4 py-3">L/h</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Responsável</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  Nenhum abastecimento no período.
                </td>
              </tr>
            ) : (
              filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {resolveMaquinaNome(maquinarios, a.maquinaId, a.maquinaNome)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{formatNumber(a.litros, 1)} L</td>
                  <td className="px-4 py-3 tabular-nums">R$ {formatNumber(a.valorTotal, 2)}</td>
                  <td className="px-4 py-3">{COMBUSTIVEL_LABELS[a.tipoCombustivel]}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {(() => {
                      const lph = lphPorId.get(a.id);
                      return lph != null ? `${formatNumber(lph, 1)} L/h` : "—";
                    })()}
                  </td>
                  <td className="px-4 py-3">{formatDate(a.data)}</td>
                  <td className="px-4 py-3 text-slate-600">{a.usuarioNome}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(a)}
                        leftIcon={<Pencil className="h-3.5 w-3.5" />}
                      >
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(a)}
                        leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
